import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { useColor } from "@styles/useColor";
import { type Curves } from "@taquito/signer";
import {
  type ChainFullId,
  ChainFullIds,
  type ChainId,
  ChainIds,
  ChainNamespaces,
  defaultDerivationPathTemplates,
  isChainId,
  parseChainFullId,
} from "@umami/chains";
import capitalize from "lodash/capitalize";
import { useFormContext } from "react-hook-form";

import { getChainValidator } from "./validationSchema";

import { RadioButtons } from "@/components/RadioButtons";

export const CURVES = ["ed25519", "secp256k1", "p256"];

export const AdvancedAccountSettings = ({
  isCurveEnabled = false,
}: {
  isCurveEnabled?: boolean;
}) => {
  const color = useColor();

  const {
    formState: { errors },
    register,
    resetField,
    watch,
    getValues,
  } = useFormContext<{
    derivationPaths?: Record<ChainId, string>;
    chainId?: ChainFullId;
    derivationPath?: string;
    curve?: Curves;
  }>();

  const validateDerivationPath = (chainId: ChainId) => (value: string) => {
    const validator = getChainValidator(chainId);

    const result = validator.safeParse(value);

    return result.success || result.error.errors[0]?.message;
  };

  const chainId = watch("chainId") ?? ChainFullIds.TEZOS;
  const { namespace } = parseChainFullId(chainId);
  const showCurves = isCurveEnabled && namespace === ChainNamespaces.TEZOS;
  const derivationPaths = getValues("derivationPaths");
  const derivationPath = getValues("derivationPath");

  return (
    <Accordion marginTop="6px" allowToggle data-testid="advanced-section">
      <AccordionItem>
        <AccordionButton
          justifyContent="center"
          color={color("900")}
          borderBottom="1px solid"
          borderBottomColor={color("100")}
          _expanded={{ borderBottomColor: "transparent" }}
        >
          <Heading size="md">Advanced</Heading>
          <AccordionIcon />
        </AccordionButton>

        <AccordionPanel>
          <Flex flexDirection="column" gap="24px">
            {showCurves && (
              <FormControl isInvalid={!!errors.curve}>
                <FormLabel>Elliptic curve</FormLabel>
                <Flex gap="8px">
                  <RadioButtons fontSize="sm" fontWeight="400" inputName="curve" options={CURVES} />
                </Flex>
              </FormControl>
            )}
            {derivationPath && (
              <FormControl isInvalid={!!errors.derivationPath}>
                <FormLabel>Derivation path</FormLabel>

                <InputGroup>
                  <Input
                    {...register("derivationPath")}
                    data-testid="derivation-path-input"
                    placeholder={derivationPath}
                  />
                  <InputRightElement>
                    <Button
                      marginRight="10px"
                      borderRadius="4px"
                      onClick={() => resetField("derivationPath")}
                      size="sm"
                      variant="inputElement"
                    >
                      Reset
                    </Button>
                  </InputRightElement>
                </InputGroup>
                {errors.derivationPath && (
                  <FormErrorMessage>{errors.derivationPath.message}</FormErrorMessage>
                )}
              </FormControl>
            )}
            {derivationPaths &&
              Object.keys(derivationPaths).map(chainId => {
                // TODO: remove this once we added BSC provider
                if (!isChainId(chainId) || chainId === ChainIds.BSC) {
                  return null;
                }

                return (
                  <FormControl key={chainId} isInvalid={!!errors.derivationPaths?.[chainId]}>
                    <FormLabel>{capitalize(chainId)} derivation path</FormLabel>

                    <InputGroup>
                      <Input
                        {...register(`derivationPaths.${chainId}`, {
                          validate: validateDerivationPath(chainId),
                        })}
                        data-testid={`derivation-path-input-${chainId}`}
                        placeholder={defaultDerivationPathTemplates[chainId]}
                      />
                      <InputRightElement>
                        <Button
                          marginRight="10px"
                          borderRadius="4px"
                          onClick={() => resetField(`derivationPaths.${chainId}`)}
                          size="sm"
                          variant="inputElement"
                        >
                          Reset
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                    {errors.derivationPaths?.[chainId] && (
                      <FormErrorMessage>{errors.derivationPaths[chainId].message}</FormErrorMessage>
                    )}
                  </FormControl>
                );
              })}
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
